import {
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import {
  AdminPanelSettings as AdminIcon,
  NoteAdd as AdmissionAddIcon,
  Timeline as AnalyticsIcon,
  People as AttendersIcon,
  Close as CloseIcon,
  Assessment as CountIcon,
  LocalHospital as DoctorIcon,
  Business as FloorIcon,
  MedicalServices as MedicineIcon,
  PersonAdd as PatientAddIcon,
  Accessibility as PatientIcon,
  Badge as StaffIcon,
  TrendingUp as TodayIcon,
  Emergency as TraumaIcon,
  Hotel as WardIcon,
} from "@mui/icons-material";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_LINK } from "../common/menu/menuItems/menuList";
import { fetchAdmissions } from "../redux/admissions/admissionsActions";
import { fetchAdminCountRequest, getPatientCountRequest } from "../redux/dashboard/dashboardAction";
import {
  fetchHistoryPatients,
  fetchPatients,
} from "../redux/patient/patientAction";
import { fetchSecurityOfficers } from "../redux/secuityOfficer/securityAction";
import ActivityTable from "./admindashboard/ActivityTable";
import PatientTable from "./admindashboard/PatientTable";
import SecurityTable from "./admindashboard/SecurityTable";
import StatCard from "./admindashboard/StatCard";

const Dashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientData, setPatientData] = useState([]);
  const [admissionData, setAdmissionData] = useState([]);
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [countType, setCountType] = useState(1);

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
      case 4:
        return "Death";
      case 5:
        return "Abscond";
      case 6:
        return "Referral";
      case 7:
        return "AMA";
      default:
        return "Unknown";
    }
  };

  // Redux state selectors
  const patientList = useSelector(
    (state) => state?.patients?.fetchListPatients || {}
  );

  const historyState = useSelector(
    (state) => state?.patients?.historyPatient || {}
  );

  const adminState = useSelector(
    (state) => state?.dashboard?.fetchAdminCount || {}
  );
  
  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  
  const patientCountState = useSelector(
    (state) => state?.dashboard?.fetchPatientCount || {}
  );

  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAdmissionsAll || {}
  );
  // Extract data from Redux state
  const patientCountData = patientCountState.count || {};
  const securityOfficerList = securityOfficerState?.data?.data || [];
  const countData = adminState?.count?.count || {};
  const historyData = historyState?.data?.data || [];


  // Process patient counts from API response
  const getPatientCounts = () => {
    const totalCounts = patientCountData.totalCounts || [];
    const todayCounts = patientCountData.todayCounts || [];

    return {
      blocks: totalCounts.map(item => ({
        name: item.block_name,
        patients: item.patientCount,
        id: item.block_id,
        todayCount: todayCounts.find(today => today.block_id === item.block_id)?.patientCount || 0
      })),
      floors: [], // Will be populated when countType is 2
      wards: []   // Will be populated when countType is 3
    };
  };

  const patientCounts = getPatientCounts();

  const dashboardStats = [
    {
      title: "Total Doctors",
      count: countData.doctorCount || 0,
      icon: <DoctorIcon sx={{ fontSize: 40 }} />,
      color: "#2196f3", 
    },
    {
      title: "Total Patients",
      count: countData.patientsCount || 0,
      icon: <PatientIcon sx={{ fontSize: 40 }} />,
      color: "#673ab7",
    },
    {
      title: "Total Staff",
      count: countData.staffsCount || 0,
      icon: <StaffIcon sx={{ fontSize: 40 }} />,
      color: "#ff9800",
    },
    {
      title: "Admin",
      count: 1,
      icon: <AdminIcon sx={{ fontSize: 40 }} />,
      color: "#4caf50",
    },
  ];

  const todayStats = [
    {
      title: "Total users",
      count: countData.totalUsersCount || 0,
      icon: <TodayIcon sx={{ fontSize: 40 }} />,
      color: "#e91e63",
    },
    {
      title: "Total Security Officers",
      count: countData.securityCount || 0,
      icon: <AttendersIcon sx={{ fontSize: 40 }} />,
      color: "#009688",
    },
    {
      title: "Today's Patients",
      count: countData.todayPatientsCount || 0,
      icon: <TodayIcon sx={{ fontSize: 40 }} />,
      color: "#e91e63",
    },
    {
      title: "Today's Attenders",
      count: countData.todayattenderCount || 0,
      icon: <AttendersIcon sx={{ fontSize: 40 }} />,
      color: "#009688",
    },
  ];

  // Event handlers
  const handleAddPatient = () => {
    navigate(APP_LINK.ADDPATIENT);
  };

  const handleAddAdmission = () => {
    navigate(APP_LINK.ADMISSIONDETAILS);
  };
  
  const handleReport = () => {
    navigate(APP_LINK.DAILYREPORT);
  };

  const handleCountDialog = () => {
    setCountDialogOpen(true);
  };

  const handleFloorWiseCount = () => {
    dispatch(getPatientCountRequest(2));
    setCountType(2);
    setSelectedTab(1);
  };

  const handleWardWiseCount = () => {
    dispatch(getPatientCountRequest(3));
    setCountType(3);
    setSelectedTab(2);
  };
  
  const handleBlockWiseCount = () => {
    dispatch(getPatientCountRequest(1));
    setCountType(1);
    setSelectedTab(0);
  };

  // Effects
  useEffect(() => {
    dispatch(fetchAdminCountRequest());
    dispatch(getPatientCountRequest(countType));
  }, [dispatch]);

  const page = 1;
  const rowsPerPage = 10;
  const searchTerm = "";

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        pageNo: page,
        pagesize: rowsPerPage,
        pagination_required: false,
        search: searchTerm,
      };
      await dispatch(fetchPatients(currentPage));
      await dispatch(fetchAdminCountRequest(currentPage));
      await dispatch(fetchSecurityOfficers(payload));
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchHistoryPatients(currentPage));
  }, [dispatch, currentPage]);

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        pageNo: page,
        pagesize: rowsPerPage,
        pagination_required: false,
        search: searchTerm,
      };
      await dispatch(fetchAdmissions(payload));
    };
    fetchData();
  }, [dispatch]);

  // Process admissions data
  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.admission_id,
        admissionNo: admission.admission_no,
        patientId: admission.patient_id,
        patientSerialNo: admission.patient_serial_no || "N/A",
        patientName: admission.patient_name,
        ipNumber: admission.ip_number,
        phone: admission.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockName: admission.block || "N/A",
        floorName: admission.floor || "N/A",
        departmentName: admission.department || "N/A",
        wardName: admission.ward || "N/A",
        status: getStatusLabel(admission.status),
        numAttenders: admission.attenders || 0,
        attenders: admission.Attenders || [],
      }));
      setAdmissionData(formattedData);
    } else {
      setAdmissionData([]);
    }
  }, [admissionsList]);

  // Process patient data
  useEffect(() => {
    if (patientList.data && Array.isArray(patientList.data.data)) {
      const formattedData = patientList.data.data.map((patient) => ({
        ids: patient.patient_serial_no,
        id: patient.id,
        name: patient.patient_name,
        ipNumber: patient.ip_number,
        phone: patient.phone,
        joinDate: patient.createdAt,
        dischargeDate: patient.discharge_date || "N/A",
        status: getStatusLabel(patient.admission_status),
      }));
      setPatientData(formattedData);
    } else {
      setPatientData([]);
    }
  }, [patientList]);

  // Process history data
  useEffect(() => {
    if (historyData.length > 0) {
      const processedData = historyData.map((item) => ({
        id: item.id,
        serialNumber: item.Patient?.patient_serial_no || "Unknown",
        patientName: item.Patient?.patient_name || "Unknown",
        attendantName: item.Attender?.attender_name || "Unknown",
        status: item.attender_status === 2 ? "In" : "Out",
        block: item.Block?.block_name || "Unknown",
        floor: item.Floor?.floor_name || "Unknown",
        ward: item.Ward?.ward_name || "Unknown",
        phone: item.Attender?.attender_phone || "Unknown",
      }));

      setFilteredData(processedData);
    }
  }, [historyData]);

  // Animation variants
  const containerVariant = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const buttonVariant = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.1)",
    },
    tap: {
      scale: 0.95,
    },
  };

  const TodayStatCard = ({ stat }) => (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 3,
        background: `linear-gradient(135deg, ${stat.color}22 0%, ${stat.color}11 100%)`,
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${stat.color}33`,
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: `0 10px 20px ${stat.color}33`,
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -15,
          right: -15,
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: `${stat.color}22`,
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 700, color: stat.color }}
          >
            {stat.count}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            {stat.title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: stat.color,
            width: 56,
            height: 56,
            color: "white",
          }}
        >
          {stat.icon}
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Divider />
        <Typography
          variant="body2"
          sx={{
            pt: 1,
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Activity Today
        </Typography>
      </Box>
    </Paper>
  );

  // Count Dialog Component
  const CountDialog = () => {
    const handleTabChange = (event, newValue) => {
      setSelectedTab(newValue);
      if (newValue === 0) {
        handleBlockWiseCount();
      } else if (newValue === 1) {
        handleFloorWiseCount();
      } else if (newValue === 2) {
        handleWardWiseCount();
      }
    };

    const PatientCountCard = ({ item, icon, color, index }) => (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
      >
        <Card
          sx={{
            background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
            border: `2px solid ${color}30`,
            borderRadius: 3,
            height: "100%",
            display: "flex",
            alignItems: "center",
            p: 2,
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: `0 8px 25px ${color}40`,
            },
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
        >
          <Avatar
            sx={{
              bgcolor: color,
              width: 50,
              height: 50,
              mr: 3,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 0.5 }}>
              {item.patients || item.patientCount}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
              {item.name || item.block_name || item.floor_name || item.ward_name}
            </Typography>
            {item.block_name && item.floor_name && selectedTab === 1 && (
              <Box>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '0.75rem' }}>
                  Block: {item.block_name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '0.75rem' }}>
                  Floor: {item.floor_name}
                </Typography>
              </Box>
            )}
            {item.ward_name && item.floor_name && item.block_name && selectedTab === 2 && (
              <Box>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '0.75rem' }}>
                  Floor: {item.floor_name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: '0.75rem' }}>
                  Block: {item.block_name}
                </Typography>
              </Box>
            )}
            {item.todayCount !== undefined && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Today: {item.todayCount}
              </Typography>
            )}
          </Box>
        </Card>
      </motion.div>
    );

    const getCurrentData = () => {
      const counts = patientCountData.totalCounts || [];
      const todayCounts = patientCountData.todayCounts || [];
      
      switch (selectedTab) {
        case 0: // Blocks
          // Group by block_id to avoid duplicates and sum patient counts
          const blockMap = new Map();
          counts.forEach(item => {
            if (item.block_name && item.block_id) {
              if (blockMap.has(item.block_id)) {
                blockMap.get(item.block_id).patientCount += item.patientCount;
              } else {
                blockMap.set(item.block_id, {
                  block_id: item.block_id,
                  block_name: item.block_name,
                  patientCount: item.patientCount,
                  todayCount: todayCounts.find(today => today.block_id === item.block_id)?.patientCount || 0
                });
              }
            }
          });
          return Array.from(blockMap.values());
          
        case 1: // Floors
          // Group by floor_id to avoid duplicates and sum patient counts
          const floorMap = new Map();
          counts.forEach(item => {
            if (item.floor_name && item.floor_id) {
              if (floorMap.has(item.floor_id)) {
                floorMap.get(item.floor_id).patientCount += item.patientCount;
              } else {
                floorMap.set(item.floor_id, {
                  floor_id: item.floor_id,
                  floor_name: item.floor_name,
                  block_name: item.block_name,
                  patientCount: item.patientCount,
                  todayCount: todayCounts.find(today => today.floor_id === item.floor_id)?.patientCount || 0
                });
              }
            }
          });
          return Array.from(floorMap.values());
          
        case 2: // Wards
          return counts.filter(item => item.ward_name && item.ward_id).map(item => ({
            ...item,
            todayCount: todayCounts.find(today => today.ward_id === item.ward_id)?.patientCount || 0
          }));
          
        default:
          return counts;
      }
    };

    const currentData = getCurrentData();

    return (
      <Dialog
        open={countDialogOpen}
        onClose={() => setCountDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            p: 3,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AnalyticsIcon sx={{ mr: 2, fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Detailed Hospital Analytics
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Comprehensive view of blocks, floors, and ward distributions
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setCountDialogOpen(false)}
              sx={{
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              bgcolor: "white",
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "1rem",
              },
            }}
          >
            <Tab
              icon={<MedicineIcon />}
              label="Block Wise"
              iconPosition="start"
            />
            <Tab
              icon={<FloorIcon />}
              label="Floor Wise"
              iconPosition="start"
            />
            <Tab
              icon={<WardIcon />}
              label="Ward Wise"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#333" }}>
                  Patient Count by {selectedTab === 0 ? 'Block' : selectedTab === 1 ? 'Floor' : 'Ward'}
                </Typography>
                
                {currentData.length > 0 ? (
                  <Grid container spacing={3}>
                    {currentData.map((item, index) => (
                      <Grid item xs={12} sm={6} md={selectedTab === 2 ? 6 : 4} key={item.block_id || item.floor_id || item.ward_id || index}>
                        <PatientCountCard
                          item={item}
                          icon={selectedTab === 0 ? <MedicineIcon /> : selectedTab === 1 ? <FloorIcon /> : <WardIcon />}
                          color={`hsl(${(index * 45) % 360}, 65%, 55%)`}
                          index={index}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No data available for {selectedTab === 0 ? 'blocks' : selectedTab === 1 ? 'floors' : 'wards'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Data will appear here when available from the API
                    </Typography>
                  </Box>
                )}
              </motion.div>
            </AnimatePresence>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box>
      <Box
        component="main"
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          width: "100%",
        }}
      >
        {/* Header with title and action buttons */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#222",
                textShadow: "0px 2px 3px rgba(0,0,0,0.1)",
                position: "relative",
                mb: { xs: 3, md: 0 },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: -10,
                  width: 60,
                  height: 4,
                  borderRadius: 2,
                  background: "#129bab",
                },
              }}
            >
              Inpatient Attendee Management Dashboard
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, md: 0 }, flexWrap: "wrap" }}>
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariant}
              >
                <Button
                  variant="contained"
                  startIcon={<CountIcon />}
                  onClick={handleCountDialog}
                  sx={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    color: "white",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    },
                  }}
                >
                  View Counts
                </Button>
              </motion.div>
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariant}
              >
                <Button
                  variant="contained"
                  startIcon={<PatientAddIcon />}
                  onClick={handleReport}
                  sx={{
                    background: "#e91e63",
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: "0 4px 10px rgba(243, 33, 54, 0.3)",
                    color: "white",
                    "&:hover": {
                      background: "#1976d2",
                    },
                  }}
                >
                  Report
                </Button>
              </motion.div>
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariant}
              >
                <Button
                  variant="contained"
                  startIcon={<PatientAddIcon />}
                  onClick={handleAddPatient}
                  sx={{
                    background: "#2196f3",
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: "0 4px 10px rgba(33, 150, 243, 0.3)",
                    color: "white",
                    "&:hover": {
                      background: "#1976d2",
                    },
                  }}
                >
                  Add Patient
                </Button>
              </motion.div>

              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariant}
              >
                <Button
                  variant="contained"
                  startIcon={<AdmissionAddIcon />}
                  onClick={handleAddAdmission}
                  sx={{
                    background: "#ff6f00",
                    color: "white",
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: "0 4px 10px rgba(255, 111, 0, 0.3)",
                    "&:hover": {
                      background: "#e65100",
                    },
                  }}
                >
                  New Admission
                </Button>
              </motion.div>
            </Box>
          </Box>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          variants={containerVariant}
          initial="hidden"
          animate="visible"
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              color: "#333",
            }}
          >
            <DoctorIcon sx={{ mr: 1, color: "#2196f3" }} />
            Hospital Overview
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {dashboardStats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={itemVariant}>
                  <StatCard stat={stat} index={index} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Today's Stats - New Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                color: "#333",
              }}
            >
              <TodayIcon sx={{ mr: 1, color: "#e91e63" }} />
              Today's Activity
            </Typography>

            <Grid container spacing={3}>
              {todayStats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={`today-${index}`}>
                  <TodayStatCard stat={stat} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <PatientTable 
                patientData={patientData.slice(0, 5)} 
                loading={patientList.inProgress}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SecurityTable
                securityOfficerList={securityOfficerList.slice(0, 5)}
                loading={securityOfficerState.inProgress}
              />
            </Grid>
          </Grid>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5 }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ActivityTable 
                admissionData={admissionData.slice(0, 5)} 
                loading={admissionsList.inProgress}
              />
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* Count Dialog */}
      <CountDialog />
    </Box>
  );
};

export default Dashboard;